import { generateCid, parseUtmParams, isUtmExpired, isCidExpired } from '~/utils/tracking'

const STORAGE_KEYS = {
  CID: '_tracking_cid',
  CID_TIMESTAMP: '_tracking_cidTimestamp',
  UTM_PARAMS: '_tracking_utmParams',
  UTM_TIMESTAMP: '_tracking_utmTimestamp',
}

/**
 * Create a tracking store for managing CID and UTM parameters
 * @param {string} siteHandle - The Craft CMS site handle for CID prefix
 * @returns {Object} Alpine-compatible store object
 */
export function createTrackingStore(siteHandle) {
  return {
    cid: null,
    cidTimestamp: null,
    utmParams: null,
    utmTimestamp: null,

    init() {
      // Load or generate CID (expires after 24 hours)
      const storedCid = this._getFromStorage(STORAGE_KEYS.CID)
      const storedCidTimestamp = this._getFromStorage(STORAGE_KEYS.CID_TIMESTAMP)

      if (storedCid && !isCidExpired(storedCidTimestamp)) {
        // Use stored CID if not expired
        this.cid = storedCid
        this.cidTimestamp = storedCidTimestamp
      } else {
        // Generate new CID and timestamp
        this.cid = generateCid(siteHandle)
        this.cidTimestamp = Date.now()
        this._setToStorage(STORAGE_KEYS.CID, this.cid)
        this._setToStorage(STORAGE_KEYS.CID_TIMESTAMP, this.cidTimestamp)
      }

      // Check for UTM params in current URL
      const urlUtmParams = parseUtmParams(window.location.search)

      // Load stored UTM data
      const storedUtmParams = this._getFromStorage(STORAGE_KEYS.UTM_PARAMS)
      const storedUtmTimestamp = this._getFromStorage(STORAGE_KEYS.UTM_TIMESTAMP)

      // If URL has UTM params, always update (fresh or changed)
      if (urlUtmParams) {
        this.utmParams = urlUtmParams
        this.utmTimestamp = Date.now()
        this._setToStorage(STORAGE_KEYS.UTM_PARAMS, this.utmParams)
        this._setToStorage(STORAGE_KEYS.UTM_TIMESTAMP, this.utmTimestamp)
      } else if (storedUtmParams && !isUtmExpired(storedUtmTimestamp)) {
        // Use stored params if not expired
        this.utmParams = storedUtmParams
        this.utmTimestamp = storedUtmTimestamp
      } else {
        // Clear expired params
        this._clearUtmData()
      }
    },

    _getFromStorage(key) {
      try {
        const value = localStorage.getItem(key)
        return value ? JSON.parse(value) : null
      } catch {
        return null
      }
    },

    _setToStorage(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch {
        // Storage full or unavailable
      }
    },

    /**
     * Get the CID for form submission
     * @returns {string|null}
     */
    getCid() {
      return this.cid
    },

    /**
     * Get UTM params as JSON string for form submission
     * Checks expiry and clears if expired
     * @returns {string|null}
     */
    getUtmParams() {
      if (!this.utmParams) {
        return null
      }

      if (isUtmExpired(this.utmTimestamp)) {
        this._clearUtmData()
        return null
      }

      return JSON.stringify(this.utmParams)
    },

    getContextForDataLayer() {
      return {
        tracking_cid: this.getCid(),
        utm_present: Boolean(this.getUtmParams()),
      }
    },

    _clearUtmData() {
      this.utmParams = null
      this.utmTimestamp = null
      localStorage.removeItem(STORAGE_KEYS.UTM_PARAMS)
      localStorage.removeItem(STORAGE_KEYS.UTM_TIMESTAMP)
    },
  }
}
